/**
 * De menuschermen: de achtergrondplaten en het solo-opzetscherm.
 *
 * Het origineel tekende deze op **640×480**, niet op de 1024×768 van het spelscherm — de
 * menu's zijn een aparte toepassing (`MainMenu.exe`). Wij tekenen ze op datzelfde raster
 * en schalen het geheel met 1,6 naar ons scherm, zodat elke knop op zijn plek staat.
 *
 * Alle maten komen van `catan-ref/shots/ref/01-hoofdmenu.png` en `03-nieuw-spel.png`.
 */
import { KARAKTERS, type Karakter } from '@catan/contracts';
import { Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import { asset, hasAsset, klik } from './assets.js';
import { RASTER_BREED, RASTER_HOOG } from './skin.js';

/** Het raster waarop de menu's van de cd tekenden. */
const MENU_BREED = 640;
const MENU_HOOG = 480;

/** De knopplaten van het menu: rust en muis-erover. */
const KNOP = { rust: 'mainmenu_001', over: 'mainmenu_002', breed: 160, hoog: 30 };

/** De letter van de menu's is schreefloos, anders dan de sierletter in het spel. */
function menuTekst(inhoud: string, grootte = 13, kleur = 0xffffff): Text {
  return new Text({
    text: inhoud,
    style: new TextStyle({
      fontFamily: 'Tahoma, "DejaVu Sans", sans-serif',
      fontSize: grootte,
      fill: kleur,
    }),
  });
}

/** Een knop op een van de originele plaatjes. */
function menuKnop(
  label: string,
  x: number,
  y: number,
  onClick: () => void,
  breed = KNOP.breed,
): Container {
  const doos = new Container();
  doos.x = x;
  doos.y = y;

  const beeld = new Sprite(Texture.from(asset('menubalken', KNOP.rust).src));
  beeld.setSize(breed, KNOP.hoog);
  doos.addChild(beeld);

  const tekst = menuTekst(label, 13);
  tekst.anchor.set(0.5, 0.5);
  tekst.x = breed / 2;
  tekst.y = KNOP.hoog / 2;
  doos.addChild(tekst);

  doos.eventMode = 'static';
  doos.cursor = 'pointer';
  doos.on('pointerover', () => {
    beeld.texture = Texture.from(asset('menubalken', KNOP.over).src);
  });
  doos.on('pointerout', () => {
    beeld.texture = Texture.from(asset('menubalken', KNOP.rust).src);
  });
  doos.on('pointertap', () => {
    klik();
    onClick();
  });
  return doos;
}

/** Zet een menuscherm van 640×480 gecentreerd en geschaald in ons raster. */
function opRaster(inhoud: Container): Container {
  const wortel = new Container();
  const factor = Math.min(RASTER_BREED / MENU_BREED, RASTER_HOOG / MENU_HOOG);
  inhoud.scale.set(factor);
  inhoud.x = (RASTER_BREED - MENU_BREED * factor) / 2;
  inhoud.y = (RASTER_HOOG - MENU_HOOG * factor) / 2;

  // Erachter zwart, zodat het niet op het bord lijkt te zweven.
  const achter = new Graphics();
  achter.rect(0, 0, RASTER_BREED, RASTER_HOOG).fill({ color: 0x000000 });
  wortel.addChild(achter, inhoud);
  wortel.eventMode = 'static';
  return wortel;
}

/**
 * Een menuplaat van 640×480 als decor, zonder knoppen.
 *
 * De hub en de inlogpagina zetten hun HTML-paneel óver dit canvas heen: het vertrouwde
 * beeld van het origineel blijft, de bediening komt van de browser. Het oude hoofdmenu
 * met zijn acht knoppen — vijf zeiden "komt later", één linkte naar catan.com — is
 * hiermee vervallen; wat werkte woont nu in de hub.
 */
export function menuAchtergrond(naam: string): Container {
  const scherm = new Container();
  if (hasAsset('menubalken', naam)) {
    const achter = new Sprite(Texture.from(asset('menubalken', naam).src));
    achter.setSize(MENU_BREED, MENU_HOOG);
    scherm.addChild(achter);
  }
  return opRaster(scherm);
}

/** Het titelscherm van het origineel (1024×768), schermvullend. Voor de voordeur. */
export function titelAchtergrond(): Container {
  const wortel = new Container();
  const achter = new Graphics();
  achter.rect(0, 0, RASTER_BREED, RASTER_HOOG).fill({ color: 0x000000 });
  wortel.addChild(achter);
  if (hasAsset('achtergrond', 'titel')) {
    const plaat = new Sprite(Texture.from(asset('achtergrond', 'titel').src));
    plaat.setSize(RASTER_BREED, RASTER_HOOG);
    wortel.addChild(plaat);
  }
  return wortel;
}

// ------------------------------------------------------------ nieuw spel

/**
 * Een uitklaplijst zoals het opzetscherm die gebruikt.
 *
 * Of hij openstaat bepaalt de aanroeper, niet de lijst zelf. Dat is nodig omdat een
 * opengeklapte lijst over de velden eronder heen valt: hij moet dus als laatste getekend
 * worden, en er mag er maar één tegelijk open zijn. Beide beslissingen liggen buiten het
 * bereik van één lijstje.
 */
function keuzelijst(opties: {
  readonly waarden: readonly string[];
  readonly gekozen: number;
  readonly x: number;
  readonly y: number;
  readonly breed: number;
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onKies: (index: number) => void;
}): Container {
  const { waarden, gekozen, breed } = opties;
  const doos = new Container();
  doos.x = opties.x;
  doos.y = opties.y;

  const veld = new Graphics();
  veld.rect(0, 0, breed, 23).fill({ color: 0x1c2b22 }).stroke({ color: 0xc9a227, width: 1 });
  doos.addChild(veld);

  const label = menuTekst(waarden[gekozen] ?? '', 13, 0xffd257);
  label.x = 6;
  label.y = 4;
  doos.addChild(label);

  // Het pijltje rechts opent de lijst.
  const knopje = new Graphics();
  knopje
    .rect(breed - 22, 2, 19, 19)
    .fill({ color: 0x3c4a3c })
    .stroke({ color: 0xc9a227, width: 1 });
  doos.addChild(knopje);
  const pijl = menuTekst(opties.open ? '▲' : '▼', 11, 0xffd257);
  pijl.x = breed - 18;
  pijl.y = 5;
  doos.addChild(pijl);

  doos.eventMode = 'static';
  doos.cursor = 'pointer';
  doos.on('pointertap', () => {
    klik();
    opties.onOpen();
  });

  if (!opties.open) return doos;

  for (const [i, waarde] of waarden.entries()) {
    const rij = new Container();
    rij.y = 23 + i * 21;
    const vlak = new Graphics();
    vlak
      .rect(0, 0, breed, 21)
      .fill({ color: i === gekozen ? 0x2f6d18 : 0x152018 })
      .stroke({ color: 0xc9a227, width: 1 });
    rij.addChild(vlak);
    const t = menuTekst(waarde, 13, 0xffd257);
    t.x = 6;
    t.y = 3;
    rij.addChild(t);
    rij.eventMode = 'static';
    rij.cursor = 'pointer';
    rij.on('pointertap', (e) => {
      e.stopPropagation();
      klik();
      opties.onKies(i);
    });
    doos.addChild(rij);
  }
  return doos;
}

/**
 * Waar de vakken op `mainmenu_005` staan, opgemeten op de plaat zelf.
 *
 * De plaat is niet zomaar een achtergrond: de kaders zijn erin getekend. Onze velden
 * stonden er een stuk of vijftien pixels naast, en dan zie je twee randen door elkaar —
 * een gouden lijn dwars door "Jouw speelfiguur" heen, en een leeg kader eronder waar niets
 * in staat. Deze getallen komen uit een scan van de plaat op gouden pixelrijen, dus ze
 * kunnen niet meer wegdrijven.
 *
 * De plaat heeft **vier** vakken: moeilijkheidsgraad, tegenstander, naam en speelfiguur.
 * Meer paste er in het origineel niet, en meer hoort er ook niet — de vraag over de
 * startlandschappen is de eerste stap van het potje, geen vijfde veld.
 */
const VELD = {
  x: 298,
  breed: 278,
  hoog: 23,
  /** De bovenkant van de vier vakken. */
  rijen: [48, 98, 273, 323],
  uitlegX: 62,
  uitlegY: 158,
  uitlegBreed: 514,
  uitlegHoog: 53,
  portretX: 60,
  portretBovenY: 27,
  portretOnderY: 251,
  portretBreed: 133,
  portretHoog: 115,
} as const;

export interface NieuwSpelOpties {
  readonly onStart: (keuze: {
    tegenstander: Karakter;
    figuur: Karakter;
    naam: string;
    moeilijkheid: string;
  }) => void;
  readonly onTerug: () => void;
  /** Je gebruikersnaam; die staat in het naamveld waar vroeger 'Speler1' stond. */
  readonly naam?: string | undefined;
}

/**
 * Het opzetscherm: moeilijkheidsgraad, tegenstander met portret en beschrijving, en je
 * eigen naam en speelfiguur.
 *
 * De vraag of je je startlandschappen zelf neerlegt stond hier ook even, als vierde
 * keuzelijst. Dat is niet waar hij hoort: in het origineel is het de eerste stap ná
 * "Spel starten", niet een instelling die je vooraf invult.
 *
 * Je naam en je speelfiguur zijn twee dingen — je heet Thijs en speelt Victoria. Zo houdt
 * het origineel het ook uit elkaar: `player.cfg` heeft een aparte `#id_player`. De naam
 * komt nu van je account; er valt hier niets meer te typen.
 *
 * De vraag over de startlandschappen stond ooit ná dit scherm als losse dialoog — en om
 * die dialoog een decor te geven maakte het spel eerst een wégwerppotje aan, dat meteen
 * daarna weer werd weggegooid. Nu is het gewoon de vierde keuze, en ontstaat het potje
 * pas als alles gekozen is.
 */
export function nieuwSpel(opties: NieuwSpelOpties): Container {
  const scherm = new Container();

  if (hasAsset('menubalken', 'mainmenu_005')) {
    const achter = new Sprite(Texture.from(asset('menubalken', 'mainmenu_005').src));
    achter.setSize(MENU_BREED, MENU_HOOG);
    scherm.addChild(achter);
  }

  const MOEILIJK = ['Beginner', 'Gevorderd', 'Meester'];
  let moeilijkheid = 1;
  /** Welke uitklaplijst openstaat, of niets. Er mag er maar één tegelijk open zijn. */
  let openLijst: 'moeilijkheid' | 'tegenstander' | 'figuur' | undefined;
  let tegen = KARAKTERS.findIndex((k) => k.naam === 'Chi');
  if (tegen < 0) tegen = 0;
  let eigen = KARAKTERS.findIndex((k) => k.naam === 'Louis');
  if (eigen < 0) eigen = 0;
  const naam = opties.naam ?? 'Speler1';

  /**
   * De figuren waar je uit kunt kiezen: iedereen behalve je tegenstander.
   *
   * Twee keer dezelfde aan tafel kan niet — dan staat hetzelfde portret boven én onder.
   * Wissel je van tegenstander terwijl je die figuur zelf hebt, dan schuift je eigen keuze
   * mee naar de eerstvolgende die vrij is.
   */
  const eigenKeuzes = (): Karakter[] => KARAKTERS.filter((_, i) => i !== tegen);
  const houdUitElkaar = (): void => {
    if (eigen === tegen) eigen = KARAKTERS.findIndex((_, i) => i !== tegen);
  };

  /** Een omlijst portret, zoals de twee kaders in het origineel. */
  const portret = (sleutel: string, x: number, y: number): Container => {
    const doos = new Container();
    doos.x = x;
    doos.y = y;
    const rand = new Graphics();
    rand.rect(0, 0, VELD.portretBreed, VELD.portretHoog)
      .fill({ color: 0x0d1a12 })
      .stroke({ color: 0xc9a227, width: 2 });
    doos.addChild(rand);
    if (hasAsset('portretten', sleutel)) {
      const beeld = new Sprite(Texture.from(asset('portretten', sleutel).src));
      beeld.setSize(VELD.portretBreed - 6, VELD.portretHoog - 6);
      beeld.x = 3;
      beeld.y = 3;
      doos.addChild(beeld);
    }
    return doos;
  };

  const laag = new Container();
  scherm.addChild(laag);

  const teken = (): void => {
    laag.removeChildren();
    const wie = KARAKTERS[tegen]!;

    const ik = KARAKTERS[eigen]!;
    laag.addChild(portret(wie.portret, VELD.portretX, VELD.portretBovenY));
    laag.addChild(portret(ik.portret, VELD.portretX, VELD.portretOnderY));

    const label = (t: string, x: number, y: number): void => {
      const el = menuTekst(t, 12, 0xffd257);
      el.x = x;
      el.y = y;
      laag.addChild(el);
    };
    for (const [tekstje, y] of [
      ['Moeilijkheidsgraad:', VELD.rijen[0]],
      ['Kies je tegenstander:', VELD.rijen[1]],
      ['Jouw naam:', VELD.rijen[2]],
      ['Jouw speelfiguur:', VELD.rijen[3]],
    ] as const) {
      label(tekstje, VELD.x, y - 16);
    }

    // De beschrijving van de gekozen tegenstander, in zijn eigen kader.
    const kader = new Graphics();
    kader
      .rect(VELD.uitlegX, VELD.uitlegY, VELD.uitlegBreed, VELD.uitlegHoog)
      .fill({ color: 0x0d1a12, alpha: 0.85 })
      .stroke({ color: 0xc9a227, width: 1 });
    laag.addChild(kader);
    const uitleg = menuTekst(wie.beschrijving, 11, 0xffe9b0);
    uitleg.style.wordWrap = true;
    uitleg.style.wordWrapWidth = VELD.uitlegBreed - 12;
    uitleg.x = VELD.uitlegX + 6;
    uitleg.y = VELD.uitlegY + 4;
    laag.addChild(uitleg);

    // Het naamveld is voorlopig een vaste waarde: typen komt met de app-schil.
    const veld = new Graphics();
    veld
      .rect(VELD.x, VELD.rijen[2], VELD.breed, VELD.hoog)
      .fill({ color: 0x1c2b22 })
      .stroke({ color: 0xc9a227, width: 1 });
    laag.addChild(veld);
    const naamTekst = menuTekst(naam, 13, 0xffd257);
    naamTekst.x = VELD.x + 6;
    naamTekst.y = VELD.rijen[2] + 4;
    laag.addChild(naamTekst);

    // De lijsten worden zo neergezet dat de openstaande als laatste komt: hij valt over
    // de velden eronder heen, en wie eerder is getekend verdwijnt erachter.
    const lijsten: readonly {
      sleutel: 'moeilijkheid' | 'tegenstander' | 'figuur';
      waarden: readonly string[];
      gekozen: number;
      y: number;
      onKies: (i: number) => void;
    }[] = [
      {
        sleutel: 'moeilijkheid',
        waarden: MOEILIJK,
        gekozen: moeilijkheid,
        y: VELD.rijen[0],
        onKies: (i) => {
          moeilijkheid = i;
        },
      },
      {
        sleutel: 'tegenstander',
        waarden: KARAKTERS.map((k) => k.naam),
        gekozen: tegen,
        y: VELD.rijen[1],
        onKies: (i) => {
          tegen = i;
          houdUitElkaar();
        },
      },
      {
        sleutel: 'figuur',
        waarden: eigenKeuzes().map((k) => k.naam),
        gekozen: Math.max(0, eigenKeuzes().findIndex((k) => k.naam === KARAKTERS[eigen]?.naam)),
        y: VELD.rijen[3],
        onKies: (i) => {
          const gekozen = eigenKeuzes()[i];
          if (gekozen !== undefined) eigen = KARAKTERS.indexOf(gekozen);
        },
      },
    ];
    const opVolgorde = [...lijsten].sort((a, b) =>
      a.sleutel === openLijst ? 1 : b.sleutel === openLijst ? -1 : 0,
    );
    for (const lijst of opVolgorde) {
      laag.addChild(
        keuzelijst({
          waarden: lijst.waarden,
          gekozen: lijst.gekozen,
          x: VELD.x,
          y: lijst.y,
          breed: VELD.breed,
          open: openLijst === lijst.sleutel,
          onOpen: () => {
            openLijst = openLijst === lijst.sleutel ? undefined : lijst.sleutel;
            teken();
          },
          onKies: (i) => {
            lijst.onKies(i);
            openLijst = undefined;
            teken();
          },
        }),
      );
    }

    laag.addChild(
      menuKnop('Spel starten', 63, 430, () => {
        opties.onStart({
          tegenstander: wie,
          figuur: ik,
          naam,
          moeilijkheid: MOEILIJK[moeilijkheid] ?? '',
        });
      }, 130),
    );
    laag.addChild(menuKnop('Annuleren', 447, 430, opties.onTerug, 130));
  };

  teken();
  return opRaster(scherm);
}
